import { Box } from '@material-ui/core';
import {
  patchAddAgentIdToChannel,
  patchChangeUserId,
  patchRemoveAgentIdFromChannel,
  User,
} from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';

import { useUserCredentials } from '@/components/modules/Users/components/AddNewUser/components/UserCredentialsActionDialog/hooks/useUserCredentials';
import { UserCredentialsType } from '@/components/modules/Users/types/UserCredentials';
import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { ChangeToAnotherChannelMessageSchema } from '@/components/widgets/AgentDetailActions/schemas/ChangeToAnotherChannelMessageSchema';
import { AgentDetailAction } from '@/components/widgets/AgentDetailActions/types/AgentDetailAction';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useRequest } from '@/hooks/requests/useRequest';
import { useUser } from '@/hooks/useUser';

interface ChangeToAnotherChannelProps {
  userCredentialsType: UserCredentialsType;
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog: () => void;
  selectedUser: User;
}

export const ChangeToAnotherChannel = ({
  userCredentialsType,
  opened,
  setOpened,
  toggleDialog,
  selectedUser,
}: ChangeToAnotherChannelProps) => {
  const { revalidateStructureAndUsers } = useUserCredentials();
  const { structure } = useUser();
  const { request, loading } = useRequest();
  const {
    state: { selectedChannel },
  } = useOperationsContext();

  const otherChannels =
    structure?.merchant.channels
      .filter((channel) => channel.id !== selectedChannel?.id)
      .map((channel) => ({
        value: channel.id,
        display: channel.description,
      })) ?? [];

  const onSubmit = async (values) => {
    if (userCredentialsType === UserCredentialsType.ADMIN) {
      await request(patchChangeUserId, { id: selectedUser.id, userId: values.channelId });
      await revalidateStructureAndUsers();
    }

    if (userCredentialsType === UserCredentialsType.AGENT && selectedChannel) {
      try {
        await request(patchAddAgentIdToChannel, { agentId: selectedUser.userId, id: values.channelId });
        await request(patchRemoveAgentIdFromChannel, { agentId: selectedUser.userId, id: selectedChannel.id });
      } catch (error) {
      } finally {
        revalidateStructureAndUsers();
      }
    }
  };

  return (
    <EditDialogWrapper
      opened={opened}
      isLoading={loading}
      setOpened={setOpened}
      handleClose={toggleDialog}
      title={AgentDetailAction.CHANGE_TO_ANOTHER_CHANNEL}
      saveButtonTextId={`${AgentDetailAction.CHANGE_TO_ANOTHER_CHANNEL}_BTN`}
      onSubmitForm={onSubmit}
      formSchema={ChangeToAnotherChannelMessageSchema}
      defaultValues={{
        currentChannel: selectedChannel?.description || '',
        channelId: '',
      }}
    >
      <>
        <Font variant={'subtitle2'} color={'textSecondary'}>
          <Text tId={`${AgentDetailAction.CHANGE_TO_ANOTHER_CHANNEL}_TEXT`} />
        </Font>
        <Box mt={3} />
        <FormGridContainer fieldVariant={FieldVariant.form} fullWidth>
          <TextFormField
            label={'CURRENT_CHANNEL'}
            name={'currentChannel'}
            defaultValue={selectedChannel?.description}
            disabled
          />
          <SelectFormField label={'NEW_CHANNEL'} name={'channelId'} selectOptions={otherChannels} />
        </FormGridContainer>
      </>
    </EditDialogWrapper>
  );
};
